import 'core-js';
import 'ulog';
import * as React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';
import { create } from 'jss';
import jssCompose from 'jss-plugin-compose';
import { ApolloProvider } from '@apollo/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Worker } from '@react-pdf-viewer/core';

import { MuiThemeProvider } from '@material-ui/core/styles';
import { createGenerateClassName, jssPreset, StylesProvider } from '@material-ui/styles';
import { getThemeByTenantId } from '@topconagriculture/theme';
import { PageTitleProvider } from '@topconagriculture/frontend-utils';
import { SocketNotificationProvider } from '@topconagriculture/react-socket-notification';
import { DashboardConfigProvider } from '@topconagriculture/dashboard';
import { PermissionsProvider } from '@topconagriculture/casbin-react';
import { ServiceWorkerListener } from '@topconagriculture/service-worker';

import packageJson from '../package.json';
import gqlInstance from './app/core/graphql/graphqlClient';
import { API_CLIENTS } from './app/config/constants';
import configureStore from './app/store/configureStore';
import PublicRouter from './app/routes/PublicRouterContainer';
import './app/config/i18n';
import { config } from './app/config';
import { createReduxHistory } from './app/store/config/history';
import { dashboardProviderConfig } from './app/config/dashboardProviderConfig';
import IsMobile from './app/shared/IsMobile/IsMobile';

const generateClassName = createGenerateClassName({
  productionPrefix: 'tap-',
});

// Create redux store with history
const initialState = {};
const store = configureStore(initialState);

const history = createReduxHistory(store);

const MOUNT_NODE = document.getElementById('root') as HTMLElement;

const theme = getThemeByTenantId(config.TENANT_ID).theme;

const jss = create({ plugins: [...jssPreset().plugins, jssCompose()] });

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false } },
});

const pdfjsVersion = packageJson.dependencies['pdfjs-dist'];

const render = (Component) => {
  return ReactDOM.render(
    <Worker workerUrl={`https://unpkg.com/pdfjs-dist@${pdfjsVersion}/build/pdf.worker.min.js`}>
      <PageTitleProvider appBaseTitle={config.APP_TITLE}>
        <StylesProvider jss={jss} generateClassName={generateClassName}>
          <MuiThemeProvider theme={theme}>
            <QueryClientProvider client={queryClient}>
              <ApolloProvider client={gqlInstance.getClient(API_CLIENTS.graphqlClient)}>
                <Provider store={store}>
                  <SocketNotificationProvider
                    baseUrl={config.NOTIFICATIONS_HOSTNAME}
                    path={config.NOTIFICATION_SUBSCRIPTION_PATH}
                    allowMultipleSameTopicSubscriptions={true}
                  >
                    <React.Suspense fallback={<p>loading</p>}>
                      <ServiceWorkerListener history={history} enabled={config.SERVICE_WORKER_ENABLED} />
                      <Router history={history}>
                        <PermissionsProvider>
                          <DashboardConfigProvider config={dashboardProviderConfig}>
                            <Component />
                            <ReactQueryDevtools />
                          </DashboardConfigProvider>
                        </PermissionsProvider>
                      </Router>
                      <IsMobile />
                    </React.Suspense>
                  </SocketNotificationProvider>
                </Provider>
              </ApolloProvider>
            </QueryClientProvider>
          </MuiThemeProvider>
        </StylesProvider>
      </PageTitleProvider>
    </Worker>,
    MOUNT_NODE,
  );
};

render(PublicRouter);

if (module.hot) {
  module.hot.accept('./app/routes/PublicRouter', () => {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const NextApp = require('./app/routes/PublicRouter').default;
    render(NextApp);
  });
}
